<template>
  <div v-loading="loading">
    <section>
      <article class="mt-5">
        <div class="container">
          <div class="mb-3">
            <el-breadcrumb separator-class="el-icon-arrow-right">
              <el-breadcrumb-item :to="{ path: '/' }">Home</el-breadcrumb-item>
              <el-breadcrumb-item>Resources</el-breadcrumb-item>
            </el-breadcrumb>
          </div>

          <h1 class="mb-4" style="font-size: 1.1em; font-weight: 600">
            Resources:
          </h1>

          <div class="search_add_section">
            <div style="width: 50%">
              <input
                type="text"
                v-model.trim="filters[0].value"
                placeholder="Search by title"
                class="search_by_input"
                spellcheck="off"
                style="width: 100%"
              />
            </div>
            <div>
              <el-button
                type="primary"
                @click="$router.push({ path: '/resources/add' })"
                >Add A Resource</el-button
              >
            </div>
          </div>
          <hr />

          <div v-loading="loading || deleting" class="table-responsive">
            <data-tables
              :data="resources"
              :table-props="tableProps"
              :pagination-props="{ pageSizes: [5, 20, 25, 50, 100] }"
              type="expand"
              :filters="filters"
            >
              <div slot="empty">
                <ScaleOut
                  v-if="isLoading && !loadingError"
                  class="mt-3"
                  :background="'#164B70'"
                />
                <div
                  @click="getResources"
                  v-else-if="!isLoading && loadingError"
                  style="cursor: pointer"
                >
                  Unable to Load Resources Now. Please click here to retry
                </div>
                <div v-if="!isLoading && !loadingError">No Resources</div>
              </div>

              <el-table-column
                label="#"
                :show-overflow-tooltip="false"
                label-class-name="text-center"
                class-name="text-center"
                width="50px"
              >
                <template slot-scope="scope">
                  <span>{{ scope.$index + 1 }}</span>
                </template>
              </el-table-column>

              <el-table-column
                label="Resource"
                :show-overflow-tooltip="false"
                label-class-name="text-left"
                width="auto"
              >
                <template slot-scope="scope">
                  <div style="display: flex; align-items: center; gap: 10px;">
                    <i 
                      class="el-icon-document" 
                      style="font-size: 50px; color: #409EFF;"
                    ></i>
                    <div>
                      <span> <strong> Title:</strong> {{ scope.row.title }} </span><br />
                      <span style="font-size: 0.85em; color: #666;">{{ scope.row.description }}</span>
                    </div>
                  </div>
                </template>
              </el-table-column>

              <el-table-column
                label="Resource File"
                :show-overflow-tooltip="false"
                label-class-name="text-center"
                class-name="text-center"
                width="180"
              >
                <template slot-scope="scope">
                  <div v-if="scope.row.file_attachment">
                    <el-button
                      @click="viewDocument(scope.row.file_attachment)"
                      type="primary"
                      size="small"
                      icon="el-icon-document"
                    >
                      Document
                    </el-button>
                  </div>
                  <div v-else-if="scope.row.video_url">
                    <el-button
                      @click="viewVideo(scope.row.video_url)"
                      type="warning"
                      size="small"
                      icon="el-icon-video-camera"
                    >
                      Video
                    </el-button>
                  </div>
                  <span v-else style="color: #999;">No file</span>
                </template>
              </el-table-column>

              <el-table-column
                label="Status"
                :show-overflow-tooltip="false"
                label-class-name="text-center"
                class-name="text-center"
                width="100"
              >
                <template slot-scope="scope">
                  <el-switch
                    v-model="scope.row.is_active"
                    @change="toggleResourceStatus(scope.row.id, scope.row.is_active)"
                    active-color="#13ce66"
                    inactive-color="#ff4949"
                  >
                  </el-switch>
                </template>
              </el-table-column>

              <el-table-column
                label="Actions"
                :show-overflow-tooltip="false"
                label-class-name="text-center"
                class-name="text-center"
              >
                <template slot-scope="scope">
                  <el-row
                    style="
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    "
                  >
                    <el-button
                      @click="
                        $router.push({ path: `/resources/edit/${scope.row.id}` })
                      "
                      type="warning"
                      icon="el-icon-edit"
                      circle
                    ></el-button>
                    <el-tooltip content="Delete" placement="top">
                      <el-button
                        v-if="$store.state.currentRole == 'ADMIN'"
                        @click="deleteResource(scope.row.id)"
                        type="danger"
                        icon="el-icon-delete"
                        circle
                      ></el-button>
                    </el-tooltip>
                  </el-row>
                </template>
              </el-table-column>
            </data-tables>
          </div>
        </div>
      </article>
    </section>

    <!-- Document Viewer Modal -->
    <el-dialog
      :title="documentModalTitle"
      :visible.sync="showDocumentModal"
      width="90%"
      top="5vh"
      :before-close="closeDocumentModal"
    >
      <div v-if="currentDocumentUrl" style="height: 80vh;">
        <!-- PDF Viewer -->
        <iframe
          v-if="isPDF"
          :src="currentDocumentUrl"
          style="width: 100%; height: 100%; border: none;"
          frameborder="0"
        ></iframe>
        
        <!-- Google Docs Viewer for Office files -->
        <iframe
          v-else-if="isOfficeDocument"
          :src="`https://docs.google.com/gview?url=${encodeURIComponent(currentDocumentUrl)}&embedded=true`"
          style="width: 100%; height: 100%; border: none;"
          frameborder="0"
        ></iframe>
        
        <!-- Fallback for unsupported formats -->
        <div v-else style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%;">
          <i class="el-icon-document" style="font-size: 80px; color: #409EFF; margin-bottom: 20px;"></i>
          <h3>{{ currentFileName }}</h3>
          <p style="color: #666; margin: 20px 0;">This file type cannot be previewed in the browser.</p>
          <el-button type="primary" size="large" @click="downloadDocument">
            <i class="el-icon-download"></i> Download File
          </el-button>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeDocumentModal">Close</el-button>
        <el-button type="primary" @click="downloadDocument">
          <i class="el-icon-download"></i> Download
        </el-button>
      </span>
    </el-dialog>

    <!-- Video Viewer Modal -->
    <el-dialog
      title="Video Viewer"
      :visible.sync="showVideoModal"
      width="80%"
      top="5vh"
      :before-close="closeVideoModal"
    >
      <div v-if="currentVideoUrl" style="height: 70vh; display: flex; align-items: center; justify-content: center;">
        <video
          :src="currentVideoUrl"
          controls
          autoplay
          style="max-width: 100%; max-height: 100%; border-radius: 8px;"
        ></video>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeVideoModal">Close</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import jwt_decode from "jwt-decode";
import ScaleOut from "@/components/scale-out-component.vue";
export default {
  components: {
    ScaleOut,
  },

  data() {
    return {
      isLoading: false,
      loading: false,
      loadingError: false,
      deleting: false,

      resources: [],

      tableData: [],
      tableProps: {
        border: true,
        stripe: true,
      },

      filters: [
        {
          prop: "title",
          value: "",
        },
      ],

      showDocumentModal: false,
      currentDocumentUrl: null,
      currentFileName: '',
      showVideoModal: false,
      currentVideoUrl: null,
    };
  },

  computed: {
    isPDF() {
      if (!this.currentDocumentUrl) return false;
      const url = this.currentDocumentUrl.toLowerCase();
      return url.endsWith('.pdf');
    },
    
    isOfficeDocument() {
      if (!this.currentDocumentUrl) return false;
      const url = this.currentDocumentUrl.toLowerCase();
      return url.endsWith('.doc') || 
             url.endsWith('.docx') || 
             url.endsWith('.xls') || 
             url.endsWith('.xlsx') || 
             url.endsWith('.ppt') || 
             url.endsWith('.pptx');
    },

    documentModalTitle() {
      if (this.isPDF) return 'PDF Viewer';
      if (this.isOfficeDocument) return 'Document Viewer';
      return 'File Preview';
    }
  },

  mounted() {
    this.setCurrentUser();
    this.getResources();
  },

  methods: {
    setCurrentUser() {
      let userToken = JSON.parse(localStorage.getItem("user"));
      let decoded = jwt_decode(userToken);
      this.$store.commit("setCurrentUsername", decoded.data.username);
      this.$store.commit("setCurrentFullName", decoded.data.fullname);
      this.$store.commit("setCurrentRole", decoded.data.role);
      this.$store.commit("setCurrentPhoto", decoded.data.image);
    },

    async getResources() {
      this.isLoading = true;
      this.isLoadingError = false;
      try {
        let request = await this.$http.get(`home/get_resources`);
        if (request.data.success) {
          this.resources = request.data.resources;
        } else throw "UNEXPECTED_RESPONSE_RECEIVED";
      } catch (error) {
        this.isLoadingError = true;
        this.$notify({
          title: "Operation Failed",
          message: "Unable to fetch Resources now, please try again",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async toggleResourceStatus(id, newStatus) {
      try {
        this.loading = true;
        let request = await this.$http.post(`home/set_resource_status/${id}`);
        
        if (
          request.data.success &&
          request.data.message === "RESOURCE_STATUS_UPDATED_SUCCESSFULLY"
        ) {
          this.$notify({
            title: "Success",
            message: `Resource ${newStatus ? 'activated' : 'deactivated'} successfully!`,
            type: "success",
          });
          // Refresh the list to ensure data consistency
          this.getResources();
        } else {
          throw "UNEXPECTED_RESPONSE";
        }
      } catch (error) {
        // Revert the switch state on error
        const resource = this.resources.find(r => r.id === id);
        if (resource) {
          resource.is_active = !newStatus;
        }
        
        if (error.message === "Network Error") {
          return this.$notify({
            title: "Connection failed",
            message: "A network error occurred, please try again.",
            type: "error",
          });
        }
        this.$notify({
          title: "Unable to Update Status",
          message: "Unable to update resource status, please try again",
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    viewDocument(fileUrl) {
      if (fileUrl) {
        this.currentDocumentUrl = fileUrl;
        this.currentFileName = fileUrl.split('/').pop();
        this.showDocumentModal = true;
      } else {
        this.$notify({
          title: "No File",
          message: "No document available for this resource",
          type: "warning",
        });
      }
    },

    closeDocumentModal() {
      this.showDocumentModal = false;
      this.currentDocumentUrl = null;
      this.currentFileName = '';
    },

    viewVideo(videoUrl) {
      if (videoUrl) {
        this.currentVideoUrl = videoUrl;
        this.showVideoModal = true;
      } else {
        this.$notify({
          title: "No Video",
          message: "No video available for this resource",
          type: "warning",
        });
      }
    },

    closeVideoModal() {
      this.showVideoModal = false;
      this.currentVideoUrl = null;
    },

    downloadDocument() {
      if (this.currentDocumentUrl) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = this.currentDocumentUrl;
        link.download = this.currentDocumentUrl.split('/').pop();
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$notify({
          title: "Download Started",
          message: "Your download has started",
          type: "success",
        });
      }
    },

    async deleteResource(id) {
      this.$confirm(
        "This will permanently delete this resource. Do you want to Continue?",
        "Confirm delete",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(async () => {
          try {
            this.deleting = true;
            let request = await this.$http.delete(`home/delete_resource/${id}`);
            if (
              request.data.success &&
              request.data.message === "RESOURCE_DELETED_SUCCESSFULLY"
            ) {
              this.getResources();
              this.$notify({
                title: "Success",
                message: "Resource deleted successfully!",
                type: "success",
              });
            } else {
              throw "UNEXPECTED_RESPONSE";
            }
          } catch (error) {
            if (error.message == "Network Error") {
              this.loadingError = true;
              return this.$notify({
                title: "Connection failed",
                message: "A network error occurred, please try again.",
                type: "error",
              });
            }
            this.$notify({
              title: "Unable to Delete",
              message: "Unable to delete Resource, Please try again",
              type: "error",
            });
          } finally {
            this.deleting = false;
          }
        })
        .catch(() => {
          //
        });
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

input[type="text"],
select,
textarea {
  width: 100%;
  padding: 10px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
  background-color: #ffffff;
  font-size: 0.9em;
}

input:focus {
  border: 0.01em solid #409eff !important;
}

.search_add_section {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>

<style>
.el-form-item {
  margin-bottom: 0px;
}

.el-form--label-top .el-form-item__label {
  float: none;
  display: inline-block;
  text-align: left;
  padding: 0 0 0px;
}
label {
  display: inline-block;
  margin-bottom: 0px;
}

/* PDF Modal Styles */
.el-dialog__wrapper .el-dialog {
  margin-top: 5vh !important;
}

.el-dialog__body {
  padding: 10px 20px;
}
</style>
